<template>
  <div class="search-box">
    <div class="search-input-wrapper">
      <Icon name="search" class="search-icon" :size="16" />
      <input
        ref="inputRef"
        v-model="searchInput"
        class="search-input"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
      <div v-if="searchInput || hasFilter" class="search-actions">
        <Button
          v-if="hasFilter"
          variant="icon"
          icon="filter"
          size="small"
          :title="filterTooltip"
          @click="$emit('filter-click')"
        />
        <Button
          v-if="searchInput"
          variant="icon"
          icon="close"
          size="small"
          title="Clear search"
          @click="clear"
        />
      </div>
    </div>
    <div v-if="showResults && (hasResults || hasActiveSearch)" class="search-results-info">
      <span class="results-count">
        {{ resultCount }} {{ resultCount === 1 ? 'result' : 'results' }}
      </span>
      <Button
        v-if="hasActiveSearch"
        variant="secondary"
        size="small"
        @click="clear"
      >
        Clear
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Button, Icon } from '@/components/atoms';

export interface SearchBoxProps {
  modelValue: string;
  placeholder?: string;
  disabled?: boolean;
  showResults?: boolean;
  resultCount?: number;
  hasFilter?: boolean;
  filterTooltip?: string;
}

const props = withDefaults(defineProps<SearchBoxProps>(), {
  placeholder: 'Search settings',
  disabled: false,
  showResults: true,
  resultCount: 0,
  hasFilter: false,
  filterTooltip: 'Filter options',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'search': [query: string];
  'focus': [event: FocusEvent];
  'blur': [event: FocusEvent];
  'clear': [];
  'filter-click': [];
  'keydown': [event: KeyboardEvent];
}>();

const inputRef = ref<HTMLInputElement>();
const searchInput = ref(props.modelValue);

const hasResults = computed(() => props.resultCount > 0);
const hasActiveSearch = computed(() => searchInput.value.trim().length > 0);

// Watch for external model changes
const updateFromModel = (newValue: string) => {
  if (searchInput.value !== newValue) {
    searchInput.value = newValue;
  }
};

// Watch for internal input changes  
const updateModel = () => {
  emit('update:modelValue', searchInput.value);
  emit('search', searchInput.value);
};

// Watch modelValue changes
watch(() => props.modelValue, updateFromModel, { immediate: true });

// Watch searchInput changes
watch(searchInput, updateModel);

const onFocus = (event: FocusEvent) => {
  emit('focus', event);
};

const onBlur = (event: FocusEvent) => {
  emit('blur', event);
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    clear();
  }
  emit('keydown', event);
};

const clear = () => {
  searchInput.value = '';
  emit('clear');
  inputRef.value?.focus();
};

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  clear,
});
</script>

<style scoped lang="scss">
.search-box {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--vscode-settings-textInputBackground);
  border: 1px solid var(--vscode-settings-textInputBorder);
  border-radius: var(--border-radius);
  
  &:focus-within {
    border-color: var(--vscode-focusBorder);
    box-shadow: 0 0 0 1px var(--vscode-focusBorder);
  }
}

.search-icon {
  position: absolute;
  left: var(--spacing-md);
  color: var(--vscode-foreground);
  opacity: 0.6;
  pointer-events: none;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) calc(var(--spacing-md) * 2 + 16px);
  color: var(--vscode-settings-textInputForeground);
  font-family: var(--vscode-font-family);
  font-size: var(--vscode-font-size);
  
  &::placeholder {
    color: var(--vscode-foreground);
    opacity: 0.6;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.search-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding-right: var(--spacing-sm);
}

.search-results-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-sm);
  background-color: var(--vscode-backgroundSecondary);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  font-size: 12px;
}

.results-count {
  color: var(--vscode-foreground);
  opacity: 0.8;
}

// Responsive adjustments
@media (max-width: 768px) {
  .search-results-info {
    flex-direction: column;
    gap: var(--spacing-sm);
    align-items: flex-start;
  }
}
</style>
